/** @jsxImportSource @emotion/react */
import {
  formatErrorResponse,
  Header,
} from '@quickbit/backoffice-core-frontend';
import {
  Input,
  Typography,
  UtilityButton,
  useTheme,
  Select,
  Option,
  Datepicker,
  Card,
  useForm,
  Snackbar,
} from '@quickbit/qb-design-system';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsync, useAsyncFn } from 'react-use';
import { MerchantSelect } from 'components';
import { createSettlementPayment } from 'tabs/Accounting/api';
import { getMerchant } from 'tabs/api';
import { CreateSettlementPaymentDetails } from '../types';
import { paymentValidators } from '../utils/paymentValidators';

type PaymentType = 'USDC' | 'EUR';

export interface CreatePaymentForm {
  paymentType: PaymentType;
  merchant: Option | null;
  paidAt: Date;
  eurAmount: number;
  comment: string | null;
}

const defaultCreatePaymentForm: CreatePaymentForm = {
  paymentType: 'EUR' as PaymentType,
  merchant: null,
  eurAmount: 0,
  paidAt: new Date(),
  comment: '',
};

interface Props {
  merchantId?: string;
  onSuccess?: (createdPayment: { paymentId: string }) => void;
}

export const CreatePayment = ({ merchantId, onSuccess }: Props) => {
  const navigate = useNavigate();
  const { spacing } = useTheme();
  const [showSnackbar, setShowSnackbar] = useState(false);

  const [{ error, loading }, createSettlementPaymentFn] = useAsyncFn(
    async (data: CreateSettlementPaymentDetails) => {
      const result = await createSettlementPayment(data);
      if (onSuccess) {
        onSuccess(result);
      } else {
        void navigate('/accounting/payments');
      }
    }
  );
  useEffect(() => {
    if (error) {
      setShowSnackbar(true);
    }
  }, [error]);
  const { fields: paymentFormFields, setters: paymentFormSetters } =
    useForm<CreatePaymentForm>(defaultCreatePaymentForm, paymentValidators);

  const usedMerchantId = merchantId || paymentFormFields.merchant?.value;

  const { value: merchant, loading: merchantLoading } = useAsync(async () => {
    if (!usedMerchantId) return null;
    return getMerchant(usedMerchantId);
  }, [usedMerchantId]);

  const disableFormSubmit =
    !usedMerchantId ||
    !paymentFormFields.eurAmount ||
    merchantLoading ||
    !merchant;

  const paymentTypeOptions = [
    { value: 'EUR', label: 'EUR' },
    ...(merchant?.config.allowCorporateWallet
      ? [{ value: 'USDC', label: 'USDC' }]
      : []),
  ];

  return (
    <>
      <Header>
        <Typography variant="heading1">Create Payment</Typography>
      </Header>
      <form
        css={{ display: 'flex', flexDirection: 'column', gap: spacing.xxs }}
      >
        {!merchantId && (
          <Card
            css={{
              display: 'flex',
              flexDirection: 'column',
              alignContent: 'left',
              marginBottom: spacing.xxxs,
            }}
          >
            <Typography variant="body">Select merchant:</Typography>
            <MerchantSelect
              merchant={paymentFormFields.merchant}
              setMerchant={(m) => {
                paymentFormSetters.merchant.set(m as Option);
              }}
            />
          </Card>
        )}

        <Select
          onSelect={(option) => {
            if (option[0] === 'USDC') {
              paymentFormSetters.paymentType.set('USDC');
            } else if (option[0] === 'EUR') {
              paymentFormSetters.paymentType.set('EUR');
            }
          }}
          label="Payment type"
          values={[paymentFormFields.paymentType]}
          options={paymentTypeOptions}
        />
        <Input
          type="number"
          label="EUR amount"
          helperText={
            paymentFormFields.paymentType === 'USDC'
              ? 'The EUR amount should correspond to the value of the USDT payment'
              : ''
          }
          value={paymentFormFields.eurAmount.toString()}
          onChange={(e) =>
            paymentFormSetters.eurAmount.set(parseFloat(e.target.value))
          }
        />

        {paymentFormFields.paymentType !== 'USDC' && (
          <Card css={{ gap: spacing.xs, marginBottom: spacing.xxxs }}>
            <Typography variant="body">Paid at:</Typography>
            <Datepicker
              title="Paid at"
              value={paymentFormFields.paidAt}
              onChange={(date: Date | null) => {
                if (date) {
                  paymentFormSetters.paidAt.set(date);
                }
              }}
              variant="primary"
              withTime={true}
            />
          </Card>
        )}
        <Input
          type="text"
          label="(Optional) Comment"
          onChange={(e) => paymentFormSetters.comment.set(e.target.value)}
          value={paymentFormFields.comment ? paymentFormFields.comment : ''}
        />
        <UtilityButton
          text="Create Payment"
          disabled={disableFormSubmit}
          loading={loading}
          onClick={() => {
            if (disableFormSubmit) {
              return;
            }
            void createSettlementPaymentFn({
              merchantId: usedMerchantId,
              euroAmount: paymentFormFields.eurAmount,
              paidAt:
                paymentFormFields.paymentType == 'USDC'
                  ? new Date().toISOString()
                  : paymentFormFields.paidAt.toISOString(),
              reference: paymentFormFields.comment,
              paymentType: paymentFormFields.paymentType,
            });
          }}
        />
        {error && (
          <Snackbar
            variant="danger"
            message={formatErrorResponse(error)}
            open={showSnackbar}
            onClose={() => setShowSnackbar(false)}
          />
        )}
      </form>
    </>
  );
};
